import { useEffect, useState } from 'react';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import type { NormalizedApiError } from '@/shared/api';

import { useCreateCategory, useUpsertCategory } from '../../hooks';
import type { CategorySummary, CategoryUpsertRequest } from '../../model/types';
import type { ToastSeverity } from '../../lib/useToast';

interface CategoryFormDialogProps {
  open: boolean;
  /** The category being edited, or `null` to create a new one. */
  category: CategorySummary | null;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

function slugify(value: string): string {
  const map: Record<string, string> = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y',
    к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f',
    х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
  };
  return value
    .toLowerCase()
    .split('')
    .map((ch) => map[ch] ?? ch)
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Create / edit a catalog category via the catalog master API. */
export function CategoryFormDialog({ open, category, onClose, onResult }: CategoryFormDialogProps) {
  const create = useCreateCategory();
  const upsert = useUpsertCategory();
  const isEdit = category !== null;

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [code, setCode] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(category?.name ?? '');
    setSlug(category?.slug ?? '');
    setCode(category?.code ?? '');
    setImageUrl(category?.imageUrl ?? '');
    setSlugTouched(isEdit);
  }, [open, category, isEdit]);

  const valid = name.trim() !== '' && slug.trim() !== '';
  const pending = create.isPending || upsert.isPending;

  const submit = () => {
    if (!valid) return;
    const body: CategoryUpsertRequest = {
      name: name.trim(),
      slug: slug.trim(),
      code: code.trim() || undefined,
      imageUrl: imageUrl.trim() || undefined,
      productCount: category?.productCount,
    };
    const handlers = {
      onSuccess: () => {
        onResult('success', isEdit ? 'Категория сохранена.' : 'Категория создана.');
        onClose();
      },
      onError: (error: NormalizedApiError) => onResult('error', error.message),
    };
    if (isEdit && category) {
      upsert.mutate({ categoryId: category.id, body }, handlers);
      return;
    }
    create.mutate(body, handlers);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{isEdit ? 'Редактировать категорию' : 'Новая категория'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label="Название"
            required
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!slugTouched) setSlug(slugify(e.target.value));
            }}
            error={name.trim() === ''}
            autoFocus
            fullWidth
          />
          <TextField
            label="Слаг (URL)"
            required
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugTouched(true);
            }}
            error={slug.trim() === ''}
            fullWidth
            slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
          />
          <TextField
            label="Код категории"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            fullWidth
          />
          <TextField
            label="URL изображения"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || pending}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
